import React from 'react';
import { useTranslate } from 'react-admin';
import {
  Box, Divider, Stack, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { DataTable, StatusDot } from '@/components/shell';
import type { IOcrTestFieldResult } from '@/types';
import { useOcrLabels } from './ocrLabels';

/** An OCR value as read or expected: monospace, and in the problem ink when it did not match. */
const OcrValue: React.FC<{ value?: string; mismatch?: boolean }> = ({ value, mismatch = false }) => {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{ fontFamily: theme.custom.text.mono, color: mismatch ? 'error.dark' : 'text.primary', overflowWrap: 'anywhere' }}
    >
      {value ?? '—'}
    </Box>
  );
};

const Match: React.FC<{ matched: boolean }> = ({ matched }) => {
  const t = useTranslate();
  return (
    <StatusDot
      tone={matched ? 'ok' : 'problem'}
      label={t(matched ? 'screens.ocrTesting.matched' : 'screens.ocrTesting.mismatched')}
    />
  );
};

/**
 * The field-by-field comparison of one OCR result against the expected values: a table on a
 * wide screen, one card per field on a phone. Shared by a run's results and a case's last run.
 */
export const FieldResultsTable: React.FC<{ results: IOcrTestFieldResult[] }> = ({ results }) => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const labels = useOcrLabels();

  if (isPhone) {
    const lines = (fr: IOcrTestFieldResult) => [
      { label: t('screens.ocrTesting.expected'), value: <OcrValue value={fr.expectedValue} /> },
      { label: t('screens.ocrTesting.actual'), value: <OcrValue value={fr.actualValue} mismatch={!fr.matched} /> },
      { label: t('screens.ocrTesting.mode'), value: labels.matchMode(fr.matchMode) },
    ];
    // Plain rows, not cards: this list sits inside a card or an unfolded result already.
    return (
      <Stack divider={<Divider />} spacing={1} aria-label={t('screens.ocrTesting.fieldResults')}>
        {results.map((fr) => (
          <Box key={fr.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0, overflowWrap: 'anywhere' }}>{fr.fieldName}</Typography>
              <Box sx={{ ml: 'auto', flexShrink: 0 }}><Match matched={fr.matched} /></Box>
            </Box>
            {lines(fr).map((line) => (
              <Box key={line.label} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Typography component="span" variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{line.label}</Typography>
                <Typography component="span" variant="body2" sx={{ minWidth: 0 }}>{line.value}</Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <DataTable label={t('screens.ocrTesting.fieldResults')}>
      <TableHead>
        <TableRow>
          <TableCell>{t('screens.ocrTesting.field')}</TableCell>
          <TableCell>{t('screens.ocrTesting.expected')}</TableCell>
          <TableCell>{t('screens.ocrTesting.actual')}</TableCell>
          <TableCell>{t('screens.ocrTesting.mode')}</TableCell>
          <TableCell>{t('screens.ocrTesting.result')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((fr) => (
          <TableRow key={fr.id}>
            <TableCell>{fr.fieldName}</TableCell>
            <TableCell><OcrValue value={fr.expectedValue} /></TableCell>
            <TableCell><OcrValue value={fr.actualValue} mismatch={!fr.matched} /></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{labels.matchMode(fr.matchMode)}</TableCell>
            <TableCell><Match matched={fr.matched} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );
};
